import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API } from "../libs/data-access/constant";
import { AppContext } from "../AppContext";
import IStats from "../libs/data-access/types";
import { useDirectusApi } from "../libs/data-access";

export interface ICenterStats {
    center_id: number
    center_name: string
    center_state: string
    sgs: number
    ags: number
    gbc: number
    ngc: number
    total: number
}

export interface IVisitEntry {
    visit_number: number;
    total_patients: number;
}

export interface IVisitCompletionByCenter {
    center_id: number;
    center_name: string;
    visits: IVisitEntry[];
}

export interface IVisitCompletionStats {
    max_visit_number: number;
    centers: IVisitCompletionByCenter[];
}

export interface ISurgeryBreakdown {
    center_id: number;
    center_name: string;
    center_state: string;
    surgery_done: number;
    surgery_not_done: number;
    total: number;
}

export default function useAnalytics() {
    const { fetchDirectData } = useDirectusApi();
    const [loading, setLoading] = useState(true);
    const [totalStats, setTotalStats] = useState<IStats | null>(null);
    const [centerWiseStats, setCenterWiseStats] = useState<ICenterStats[]>([]);
    const [maxNoOfVisits, setMaxNoOfVisits] = useState<number>(0);
    const [visitCompletedByCenter, setVisitCompletedByCenter] = useState<IVisitCompletionStats | null>(null);
    const [surgeryBreakdown, setSurgeryBreakdown] = useState<ISurgeryBreakdown[]>([]);
    const { user, role } = useContext(AppContext);

    const fetchDashboardStats = async () => {
        setLoading(true);
        try {
            // 1. Fetch Total Stats
            const totalRes = await axios.get(`${API}custom/stats${role?.includes("Center") ? "?center_id=" + user?.id : ''}`);
            setTotalStats(totalRes.data.data);
        } catch (error) {
            console.error("Error in fetching analytics", error);
        } finally {
            setLoading(false);
        }
    }

    const fetchVisitCompletionStats = async () => {
        setLoading(true);
        try {
            const visitRes = await fetchDirectData("custom/visit-completion-stats");
            const visitPayload = visitRes?.data as IVisitCompletionStats | undefined;

            if (visitPayload) {
                setMaxNoOfVisits(visitPayload.max_visit_number ?? 0);
                setVisitCompletedByCenter(visitPayload);
            }
        } catch (error) {
            console.error("Error in fetching visit completion stats", error);
        } finally {
            setLoading(false);
        }
    }



    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            // 2. Fetch center-wise disease breakdown from a single custom endpoint
            const breakdownRes = await fetchDirectData("custom/center-wise-disease-breakdown");
            const breakdown: ICenterStats[] = breakdownRes?.data ?? [];
            setCenterWiseStats(breakdown);
        } catch (error) {
            console.error("Error in fetching analytics", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSurgeryBreakdown = async () => {
        setLoading(true);
        try {
            const surgeryRes = await fetchDirectData("custom/center-wise-surgery-breakdown");
            const surgeryData: ISurgeryBreakdown[] = surgeryRes?.data ?? [];
            setSurgeryBreakdown(surgeryData);
        } catch (error) {
            console.error("Error in fetching surgery breakdown", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardStats()
        fetchAnalytics();
        fetchVisitCompletionStats();
        fetchSurgeryBreakdown();
    }, []);

    return {
        loading,
        totalStats,
        centerWiseStats,
        visitCompletedByCenter,
        refreshAnalytics: fetchAnalytics,
        maxNoOfVisits,
        surgeryBreakdown,
    };
}
